import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { api, type ScheduleItem } from "../api.ts";
import { Icon } from "./Workspace.tsx";
import "./schedule-editor.css";

const CHANNELS = [["call", "电话"], ["email", "邮件 / 消息"], ["visit", "拜访"], ["meeting", "会议"], ["prep", "准备"]] as const;
export interface SavedFollowup { dueOn: string; channel: string; note: string }

export function EditScheduleButton(props: { item: ScheduleItem; onEdit: (item: ScheduleItem) => void }): React.JSX.Element | null {
  if (!props.item.canEdit) return null;
  return <button type="button" className="schedule-edit-button" aria-label={`修改${props.item.customerName}的日程`} title="修改日程"
    onClick={() => props.onEdit(props.item)}><Icon name="edit" size={14} /></button>;
}

export function ScheduleEditor(props: { item: Pick<ScheduleItem, "customerId" | "customerName" | "dueOn" | "channel" | "note">; onClose: () => void; onSaved: (followup: SavedFollowup) => void }): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const dateInput = useRef<HTMLInputElement>(null);
  const id = useId();
  const [channel, setChannel] = useState(props.item.channel ?? "call");
  const [note, setNote] = useState(props.item.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    dateInput.current?.focus();
    return () => element?.close();
  }, []);

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    const dueOn = String(new FormData(event.currentTarget).get("dueOn") ?? "");
    setBusy(true); setError("");
    try {
      const result = await api.put<{ followup: SavedFollowup }>(`/api/customers/${props.item.customerId}/followup`, { dueOn, channel, note });
      props.onSaved(result.followup);
    } catch (err) { setError((err as Error).message); setBusy(false); }
  };

  return <dialog ref={dialog} className="schedule-editor" aria-labelledby={`${id}-title`} aria-describedby={`${id}-customer`}
    onCancel={(event) => { event.preventDefault(); if (!busy) props.onClose(); }}
    onClick={(event) => {
      const bounds = event.currentTarget.getBoundingClientRect();
      if (!busy && event.target === event.currentTarget && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) props.onClose();
    }}>
    <form onSubmit={(event) => { void save(event); }}>
      <div className="schedule-editor-heading"><div><h2 id={`${id}-title`}>修改日程</h2><p id={`${id}-customer`}>{props.item.customerName}</p></div>
        <button type="button" className="icon-button" aria-label="关闭日程编辑" disabled={busy} onClick={props.onClose}><Icon name="close" /></button>
      </div>
      <fieldset disabled={busy}>
        <label className="schedule-editor-field" htmlFor={`${id}-date`}>跟进日期<input ref={dateInput} id={`${id}-date`} name="dueOn" type="date" required defaultValue={props.item.dueOn} /></label>
        <div className="schedule-editor-field"><span id={`${id}-channel`}>跟进方式</span><div className="schedule-channel-options" role="group" aria-labelledby={`${id}-channel`}>
          {CHANNELS.map(([value, label]) => <button type="button" key={value} aria-pressed={channel === value} className={channel === value ? "on" : ""} onClick={() => setChannel(value)}>{label}</button>)}
        </div></div>
        <label className="schedule-editor-field" htmlFor={`${id}-note`}><span>备注<small className="schedule-field-optional">可选</small></span>
          <textarea id={`${id}-note`} rows={3} maxLength={500} placeholder="例如：确认方案反馈，提前准备报价资料" value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
      </fieldset>
      {error && <div className="err" role="alert">{error}</div>}
      <div className="schedule-editor-footer"><button type="button" className="b" disabled={busy} onClick={props.onClose}>取消</button>
        <button type="submit" className="b p" disabled={busy}>{busy ? "正在保存…" : "保存日程"}</button>
      </div>
    </form>
  </dialog>;
}
